<script setup lang="ts">
import ChatArea from '@/components/assistant/chat/ChatArea.vue'
import ConversationList from '@/components/assistant/ConversationList.vue'
import CreditUsageVue from '@/components/assistant/CreditUsage.vue'
import EditConversationModalDialogContent from '@/components/assistant/EditConversationModalDialogContent.vue'
import IconButton from '@/components/assistant/IconButton.vue'
import SidebarIcon from '@/components/assistant/SidebarIcon.vue'
import TextButton from '@/components/assistant/mobile/TextButton.vue'
import type {CreditUsage} from '@/client/mentor/CreditUsageClient'
import type {ConversationIntent} from '@/components/assistant/types'
import type {Conversation} from '@/client/mentor/ConversationsClient'
import {Menu as VMenu} from 'floating-vue'
import {computed, ref} from 'vue'
import {useAssistantPrint} from '@/components/assistant/composables/useAssistantPrint'
import {useAssistantStore} from '@/stores/assistant'
import {useConversationTitleFormatter} from '@/components/assistant/composables/useConversationTitleFormatter'
import {useDeleteConversation} from '@/components/assistant/composables/useDeleteConversation'
import {useEditConversation} from '@/components/assistant/composables/useEditConversation'
import {useI18n} from 'vue-i18n'

const [formatTitle] = useConversationTitleFormatter()
const {printConversation} = useAssistantPrint()
const assistantStore = useAssistantStore()
const {t} = useI18n()

const props = defineProps<{
  companyId: number
  conversations: Conversation[]
  creditUsage?: CreditUsage
  hasNextPage: boolean
  isFetchingNextPage: boolean
  intent: ConversationIntent
}>()

defineEmits<{
  pendingMessageResolved: []
}>()

const {openDeleteConversationModal} = useDeleteConversation(props.intent.deleteConversationById)
const {editConversationModalId, editTitle, editTitleError, openEditConversationModal} = useEditConversation(
  props.intent.patchConversationTitle
)

const menuOpen = ref(false)
const sidebarOpen = ref(false)

const activeConversation = computed(() => props.conversations.find((c) => c.id === assistantStore.activeConversationId))
</script>

<template>
  <div
    class="fixed left-0 top-0 w-screen h-screen bg-white z-assistant-mobile text-base assistant-modal-print"
    style="height: 100dvh"
  >
    <div class="h-full w-full flex flex-col">
      <!-- Header -->
      <div class="py-2 px-3 flex flex-row items-center gap-3 border-b-[1px] print:hidden">
        <button class="w-6 h-6 flex items-center justify-center shrink-0" @click="sidebarOpen = !sidebarOpen">
          <SidebarIcon :has-unread-conversations="assistantStore.hasUnreadConversations" />
        </button>

        <span v-if="sidebarOpen" class="font-semi-bold">{{ t('assistant.conversations') }}</span>

        <template v-else>
          <VMenu v-model:shown="menuOpen" placement="bottom-start" :show-triggers="['click']" theme="new-ui-menu">
            <button class="w-6 h-6 flex items-center justify-center shrink-0">
              <i class="fa-solid fa-ellipsis-vertical"></i>
            </button>

            <template #popper>
              <div class="flex flex-col gap-1 p-4">
                <TextButton :disabled="!activeConversation" @click="assistantStore.setActiveConversation(undefined)">{{
                  t('assistant.actions.newChat')
                }}</TextButton>
                <TextButton :disabled="!activeConversation" @click="printConversation()">{{
                  t('assistant.actions.download')
                }}</TextButton>
                <TextButton
                  :disabled="!activeConversation"
                  @click="activeConversation && openEditConversationModal(activeConversation, formatTitle(activeConversation))"
                  >{{ t('assistant.actions.rename') }}</TextButton
                >
                <TextButton
                  :disabled="!activeConversation"
                  @click="activeConversation && openDeleteConversationModal(activeConversation, formatTitle(activeConversation))"
                  >{{ t('assistant.actions.delete') }}</TextButton
                >
              </div>
            </template>
          </VMenu>
        </template>

        <IconButton
          v-tt="$t('assistant.actions.close')"
          class="ml-auto w-6 h-6 flex items-center justify-center shrink-0"
          icon-classes="fa-solid fa-xmark scale-[1.2]"
          @click="assistantStore.toggleVisible"
        />
      </div>

      <!-- Content -->
      <div class="flex-1 min-h-0">
        <Transition mode="out-in" name="assistant-mobile-content">
          <template v-if="sidebarOpen">
            <div key="conversation-list" class="h-full min-h-0 flex flex-col">
              <ConversationList
                :conversations="conversations"
                :has-next-page="hasNextPage"
                :intent="intent"
                :is-fetching-next-page="isFetchingNextPage"
                @conversation-selected="sidebarOpen = false"
              />

              <div class="p-3">
                <CreditUsageVue :credit-usage="creditUsage" />
              </div>
            </div>
          </template>

          <div v-else key="chat-area" class="w-full h-full min-h-0 flex flex-col">
            <ChatArea
              :company-id="companyId"
              :credit-usage="creditUsage"
              :intent="intent"
              @pending-message-resolved="$emit('pendingMessageResolved')"
            />
          </div>
        </Transition>
      </div>
    </div>
  </div>

  <!-- Edit conversation modal -->
  <EditConversationModalDialogContent
    v-if="editConversationModalId"
    :id="editConversationModalId"
    v-model="editTitle"
    :error="editTitleError"
  />
</template>

<style scoped>
.assistant-mobile-content-enter-active,
.assistant-mobile-content-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.assistant-mobile-content-enter-from,
.assistant-mobile-content-leave-to {
  opacity: 0;
  transform: translateX(10px);
}
</style>
